/* SUMMARY
  * Imports
  * CustomerReceivables: show a customer's overdue Odoo invoices
    * formatAmount: money in the instance's own currency
    * body: the state-dependent content
*/

/* Imports */
import { t } from "i18next";
import { Button, Card, Typography } from "@material-tailwind/react";
import { ArrowPathIcon, BanknotesIcon } from "@heroicons/react/24/outline";
import { format } from "date-fns";
import Loading from "../../loading/Loading";
import { useCustomerReceivables } from "../../../hooks/useCustomerReceivables";
/***/

/**
 * CustomerReceivables: show a customer's overdue Odoo invoices.
 *
 * @param customerId - customer to read
 * @param variant - "section" on the customer detail, "inline" beside the customer card in a form
 */
export default function CustomerReceivables({
  customerId,
  variant
}: {
  customerId?: string,
  variant: "section" | "inline"
}) {
  const { data, loading, refreshing, error, refresh, allowed } = useCustomerReceivables(customerId);

  // Hiding the block is a courtesy; the endpoint is the authority and answers 403 regardless.
  if (!allowed) return null;

  /**
   * The amount carries the instance's own currency, read from the Odoo company at sync time. The
   * product runs in Poland and Romania as well as the euro zone, so no symbol is hardcoded.
   *
   * A currency that is not a well-formed ISO 4217 code (unlike "" or null, which fall safely into
   * the plain-number branch) makes `Intl.NumberFormat` throw `RangeError`. There is no error
   * boundary anywhere in this app, so an uncaught throw here would blank the whole customer page —
   * checking the shape first keeps a bad code from taking anything down.
   */
  const formatAmount = (amount: number, currency: string | null): string =>
    new Intl.NumberFormat(localStorage.getItem("i18nextLng") || "en", currency && /^[A-Z]{3}$/.test(currency)
      ? { style: "currency", currency }
      : { maximumFractionDigits: 2 }).format(amount);

  /**
   * A line shown alongside stored figures when the last refresh failed. Never shown on its own:
   * the spec's cardinal rule is that "owes nothing" and "we do not know" must never be confused,
   * and these figures are real, just possibly stale.
   */
  const refreshFailedNotice = () => (
    <Typography data-testid="receivables-refresh-failed" variant="small" className="text-gray-500">
      {t("client:receivables:refreshFailed")}
    </Typography>
  );

  const body = () => {
    if (loading) return <Loading size="h-5 w-5"/>;

    // Only when there is nothing to fall back on: the hook keeps the previous figures on a failed
    // refresh (it sets `error` but never clears `data`), and this block must keep showing them
    // rather than replace true-but-possibly-stale numbers with "we don't know".
    if (error && !data) {
      return (
        <Typography data-testid="receivables-error" className="text-gray-500">
          {t("client:receivables:unavailable")}
        </Typography>
      );
    }

    if (!data) return null;

    if (data.status === "NOT_LINKED") {
      return (
        <Typography data-testid="receivables-not-linked" className="text-gray-500">
          {t("client:receivables:notLinked")}
        </Typography>
      );
    }

    if (data.status === "NEVER_SYNCED") {
      return (
        <Typography data-testid="receivables-never-synced" className="text-gray-500">
          {t("client:receivables:neverSynced")}
        </Typography>
      );
    }

    // An asserted zero: the customer owes nothing, which is not the same as having no information.
    if (!data.overdueCount) {
      return (
        <div className="flex flex-col gap-1">
          <Typography data-testid="receivables-settled">
            {t("client:receivables:settled")}
          </Typography>
          {error && refreshFailedNotice()}
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-1">
        <Typography data-testid="receivables-count">
          {t("client:receivables:count", { count: data.overdueCount })}
        </Typography>
        <Typography data-testid="receivables-amount" color="red" className="font-semibold">
          {formatAmount(data.overdueAmount ?? 0, data.currency)}
        </Typography>
        {error && refreshFailedNotice()}
      </div>
    );
  };

  const syncedAt = () => {
    if (!data?.syncedAt) return null;
    // A field the spec itself records as having been mis-serialised once before: date-fns'
    // `format` throws on an unparsable value, and with no error boundary in this app that would
    // blank the whole customer page rather than just this line.
    let formatted: string;
    try {
      formatted = format(new Date(data.syncedAt), "dd/MM/yyyy HH:mm");
    } catch {
      return null;
    }
    return (
      <Typography data-testid="receivables-synced-at" variant="small" className="text-gray-500">
        {t("client:receivables:syncedAt", { date: formatted })}
      </Typography>
    );
  };

  if (variant === "inline") {
    return (
      <div className="flex gap-2 mx-3 mb-3">
        <div className="flex items-center justify-center">
          <BanknotesIcon className="h-[20px] w-[20px] text-gray-400"/>
        </div>
        <div className="flex flex-col">
          {body()}
          {syncedAt()}
        </div>
      </div>
    );
  }

  return (
    <>
      <Typography variant="h4" className="sub-title">
        {t("client:receivables:title")}
      </Typography>

      <Card className="details-card">
        <div className="field col-span-2">
          {body()}
          {syncedAt()}
        </div>

        {data?.status !== "NOT_LINKED" && (
          <div className="field col-span-2">
            <Button data-testid="receivables-refresh"
                    variant="outlined"
                    size="sm"
                    disabled={refreshing || loading}
                    onClick={() => refresh()}
                    className="flex items-center gap-2 w-fit">
              <ArrowPathIcon className="h-4 w-4"/>
              {t("client:receivables:refresh")}
            </Button>
          </div>
        )}
      </Card>
    </>
  );
}
/***/
